export type ProductCandidateSource = {
  code?: string | null;
  name?: string | null;
};

export type ProductCandidate = {
  id: string;
  product_code: string;
  product_name: string | null;
};

export type RankedProductCandidate = {
  product: ProductCandidate;
  score: number;
  reason: "exact_code" | "code_similarity" | "exact_name" | "name_similarity" | "product_family";
};

/** Normalize Turkish product text without losing meaningful letters or digits. */
export function normalizeProductMatchText(value: unknown): string {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const compact = (value: unknown) => normalizeProductMatchText(value).replace(/\s/g, "");
const tokenSet = (value: unknown) => new Set(normalizeProductMatchText(value).split(" ").filter((part) => part.length >= 2));

// Variant descriptors are removed only for the secondary family-name comparison.
// SKU/model identifiers remain untouched and take precedence over this fallback.
const VARIANT_TOKENS = new Set([
  "siyah", "black", "beyaz", "white", "kirmizi", "red", "mavi", "blue",
  "lacivert", "navy", "yesil", "green", "sari", "yellow", "gri", "gray", "grey",
  "gri", "pembe", "pink", "mor", "purple", "turuncu", "orange", "kahverengi", "brown",
  "bordo", "bej", "beige", "gumus", "silver", "altin", "gold", "renk",
]);

function isSizeToken(token: string): boolean {
  return /^(?:xxs|xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl|6xl|[0-9]{1,3}(?:[.,][0-9]+)?|[0-9]{1,2}\/[0-9]{1,2})$/i.test(token);
}

/** Remove likely color/size descriptors for family comparison, not for SKU identity. */
export function normalizeProductFamilyName(value: unknown): string {
  return normalizeProductMatchText(value)
    .split(" ")
    .filter((token) => token.length > 0 && !VARIANT_TOKENS.has(token) && !isSizeToken(token))
    .join(" ");
}

function hasConflictingModelIdentifier(source: unknown, candidate: unknown): boolean {
  // Extract model/SKU identifiers from the original text before punctuation
  // normalization, otherwise FZ9754-100 and FZ9754-012 collapse to FZ9754.
  const extract = (value: unknown) => (String(value ?? "").toLocaleUpperCase("tr-TR")
    .match(/\b(?:[A-Z]{1,5}\d{3,}(?:[-/]\w+)?|\d{6,})\b/g) ?? [])
    .map((code) => code.replace(/[-/]/g, ""));
  const left = extract(source);
  const right = extract(candidate);
  return left.length > 0 && right.length > 0 && !left.some((code) => right.includes(code));
}

/**
 * Suggestions only: this function never approves or writes a mapping.
 * Exact codes rank first. Product-family comparison is a conservative fallback
 * after SKU/model conflicts have been ruled out. Ties remain visible.
 */
export function rankProductCandidates(
  source: ProductCandidateSource,
  products: ProductCandidate[],
  limit = 5,
): RankedProductCandidate[] {
  const sourceCode = compact(source.code);
  const sourceName = normalizeProductMatchText(source.name);
  const sourceFamily = normalizeProductFamilyName(source.name);
  if (!sourceCode && sourceName.length < 3) return [];

  return products.map((product) => {
    const code = compact(product.product_code);
    const name = normalizeProductMatchText(product.product_name);
    const family = normalizeProductFamilyName(product.product_name);
    let score = 0;
    let reason: RankedProductCandidate["reason"] = "name_similarity";

    if (sourceCode && code === sourceCode) {
      score = 1200;
      reason = "exact_code";
    } else if (sourceName && name && sourceName === name) {
      score = 1100;
      reason = "exact_name";
    } else if (sourceCode && code && (code.startsWith(sourceCode) || sourceCode.startsWith(code))) {
      score = 800;
      reason = "code_similarity";
    } else if (sourceName && name && hasConflictingModelIdentifier(source.name, product.product_name)) {
      score = 0;
    } else if (sourceFamily && family && sourceFamily === family && sourceFamily.length >= 5) {
      score = 760;
      reason = "product_family";
    } else if (sourceFamily && family && sourceFamily.length >= 8 && family.length >= 8 &&
      (sourceFamily.includes(family) || family.includes(sourceFamily))) {
      score = 680;
      reason = "product_family";
    } else if (sourceName && name) {
      const sourceTokens = tokenSet(sourceName);
      const candidateTokens = tokenSet(name);
      const overlap = sourceTokens.size
        ? [...sourceTokens].filter((token) => candidateTokens.has(token)).length / sourceTokens.size
        : 0;
      const containment = sourceName.length >= 8 && name.length >= 8 &&
        (sourceName.includes(name) || name.includes(sourceName));
      if (containment || overlap >= 0.5) {
        score = containment ? 700 + Math.min(name.length, 100) / 10 : Math.round(500 + overlap * 150);
        reason = "name_similarity";
      }
    }
    return { product, score, reason };
  }).filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.product.product_code.localeCompare(b.product.product_code, "tr-TR"))
    .slice(0, Math.max(1, limit));
}


/**
 * Auto-approve only when deterministic identity evidence and AI independently
 * select the same unique exact code/name candidate. Similarity/family matches
 * and ties always require human review.
 */
export function isSafeAutomaticProductMatch(
  ranked: RankedProductCandidate[],
  selectedProductId: string | null,
  aiConfidence: number,
): boolean {
  if (!selectedProductId || !Number.isFinite(aiConfidence) || aiConfidence < 0.95) return false;
  const [top, second] = ranked;
  if (!top || top.product.id !== selectedProductId) return false;
  if (top.reason !== "exact_code" && top.reason !== "exact_name") return false;
  return !second || second.score < top.score;
}
